/**
 * Camada de API — sempre fala com o backend Node em
 * `NEXT_PUBLIC_API_URL` (default `http://localhost:4000`). Sem mocks.
 *
 * Wrapper único `request<T>()` garante:
 *  - timeout configurável (default 20s)
 *  - parse seguro de JSON (resposta vazia ou HTML não derruba a tela)
 *  - injeção automática de `Authorization: Bearer <token>` quando há sessão
 *  - renovação automática do access token via `POST /auth/refresh` quando
 *    uma rota privada responde 401 (cookie httpOnly do refresh token),
 *    com promise compartilhada para evitar chamadas duplicadas
 *  - resposta padronizada em `ApiResponse<T>` mesmo em falha de rede/parse
 *  - status HTTP propagado em `error.details.status` para a UI diferenciar
 *    401, 404, 500, etc.
 *
 * Todas as funções abaixo preservam suas assinaturas originais — as telas
 * (login, dashboard, /criar, /p/[slug]) seguem usando sem alteração.
 */

import {
  type ApiResponse,
  type AuthResponse,
  type CreateGiftRequest,
  type UpdateGiftRequest,
  type CheckoutRequest,
  type ValidateCouponRequest,
  type UnlockGiftRequest,
  type AiLoveLetterRequest,
  type AiImproveTextRequest,
  type AiGenerateExperienceRequest,
  type LoginRequest,
  type RegisterRequest,
  type Gift,
  type Payment,
  type GiftTemplate,
  type AiGeneration,
  type User,
} from "@/types";

const API_URL = (process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000").replace(/\/$/, "");

const DEFAULT_TIMEOUT_MS = 20_000;
const TOKEN_KEY = "lovepage_token";
// Token de impersonação fica em sessionStorage (por tab) — quando
// existe, prevalece sobre o token do admin pra que essa aba se
// comporte como o user-alvo. Fechar a tab encerra a sessão.
const IMPERSONATION_TOKEN_KEY = "lovepage_impersonation_token";
const IMPERSONATION_INFO_KEY = "lovepage_impersonation_info";

// ─── Token helpers ────────────────────────────────────────────────────────────

function getToken(): string {
  if (typeof window === "undefined") return "";
  try {
    // Impersonação tem prioridade: enquanto o token de impersonação
    // estiver setado na sessionStorage, todas as chamadas usam ele.
    const imp = window.sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
    if (imp) return imp;
    return window.localStorage.getItem(TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export interface ImpersonationInfo {
  userId: string;
  userName: string;
  userEmail: string;
  expiresAt: number;
}

export function startImpersonation(
  accessToken: string,
  info: Omit<ImpersonationInfo, "expiresAt">,
  ttlSeconds: number
): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, accessToken);
    window.sessionStorage.setItem(
      IMPERSONATION_INFO_KEY,
      JSON.stringify({
        ...info,
        expiresAt: Date.now() + ttlSeconds * 1000,
      })
    );
  } catch {
    /* ignora */
  }
}

export function getImpersonationInfo(): ImpersonationInfo | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(IMPERSONATION_INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationInfo;
    if (parsed.expiresAt < Date.now()) {
      stopImpersonation();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function stopImpersonation(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    window.sessionStorage.removeItem(IMPERSONATION_INFO_KEY);
  } catch {
    /* ignora */
  }
}

export function isImpersonating(): boolean {
  return Boolean(getImpersonationInfo());
}

export function saveToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // storage indisponível (modo privado, quota) — segue silencioso
  }
}

export function clearToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignora
  }
}

export function isAuthenticated(): boolean {
  return Boolean(getToken());
}

// ─── Refresh token (auto-renew) ───────────────────────────────────────────────

const REFRESH_PATH = "/auth/refresh";

// Promise compartilhada: se várias requisições baterem 401 ao mesmo tempo,
// apenas uma chamada de /auth/refresh acontece e todas reaproveitam o token.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_URL}${REFRESH_PATH}`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const text = await res.text().catch(() => "");
      let body: { success?: boolean; data?: { accessToken?: string } } | null = null;
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          body = null;
        }
      }
      if (!res.ok || !body?.success || !body.data?.accessToken) {
        return null;
      }
      saveToken(body.data.accessToken);
      return body.data.accessToken;
    } catch {
      return null;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?next=${next}`;
}

// ─── Wrapper central de fetch ─────────────────────────────────────────────────

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** Quando definido, envia como JSON. Use `formData` para multipart. */
  json?: unknown;
  formData?: FormData;
  /** Se true, omite o header Authorization mesmo se houver token. */
  skipAuth?: boolean;
  timeoutMs?: number;
  /** Interno: marca tentativa pós-refresh para impedir loop de 401. */
  _retried?: boolean;
};

function buildHeaders(opts: RequestOptions): HeadersInit {
  const headers: Record<string, string> = {};
  if (opts.json !== undefined) headers["Content-Type"] = "application/json";
  if (!opts.skipAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<ApiResponse<T>> {
  const url = `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const controller = new AbortController();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      headers: buildHeaders(opts),
      body: opts.formData ?? (opts.json !== undefined ? JSON.stringify(opts.json) : undefined),
      signal: controller.signal,
      credentials: "include",
    });
  } catch (err) {
    clearTimeout(timer);
    const isAbort = (err as { name?: string })?.name === "AbortError";
    return {
      success: false,
      error: {
        code: isAbort ? "TIMEOUT" : "NETWORK_ERROR",
        message: isAbort
          ? "O servidor demorou demais para responder. Tente novamente."
          : "Não foi possível alcançar o servidor. Verifique sua conexão.",
        details: { reason: String((err as Error)?.message ?? err) },
      },
    };
  }
  clearTimeout(timer);

  // Access token expirou? Tenta /auth/refresh uma vez e repete a requisição.
  // Só vale para rotas privadas (skipAuth=false) e quando já há sessão local —
  // evita refresh inútil em chamadas anônimas (ex.: /music/search).
  if (
    res.status === 401 &&
    !opts.skipAuth &&
    !opts._retried &&
    getToken()
  ) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return request<T>(path, { ...opts, _retried: true });
    }
    // Refresh falhou: sessão perdida. Limpa e devolve o usuário ao login.
    clearToken();
    redirectToLogin();
  }

  // Lê como texto e tenta parsear — protege contra resposta vazia / HTML
  // (ex: tela de erro de gateway, redirect login do reverse proxy).
  const text = await res.text().catch(() => "");
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  // Backend sempre responde no formato `{ success, data }` ou
  // `{ success: false, error: { code, message, details? } }`.
  if (parsed && typeof parsed === "object" && "success" in (parsed as Record<string, unknown>)) {
    const body = parsed as ApiResponse<T>;
    if (body.success === false) {
      return {
        success: false,
        error: {
          code: body.error.code,
          message: body.error.message,
          details: { ...(body.error.details ?? {}), status: res.status },
        },
      };
    }
    return body;
  }

  // Resposta sem o envelope esperado — converte pra erro padronizado.
  return {
    success: false,
    error: {
      code: res.ok ? "INVALID_RESPONSE" : `HTTP_${res.status}`,
      message: res.ok
        ? "Resposta inesperada do servidor."
        : `Falha na requisição (HTTP ${res.status}).`,
      details: { status: res.status, body: text.slice(0, 200) },
    },
  };
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export async function register(req: RegisterRequest): Promise<ApiResponse<AuthResponse>> {
  return request<AuthResponse>("/auth/register", { method: "POST", json: req, skipAuth: true });
}

export async function login(req: LoginRequest): Promise<ApiResponse<AuthResponse>> {
  return request<AuthResponse>("/auth/login", { method: "POST", json: req, skipAuth: true });
}

/**
 * Cadastro sem senha (usado no checkout para reduzir fricção).
 * Backend gera uma senha aleatória internamente; o usuário pode definir
 * uma nova depois via "esqueci minha senha".
 */
export async function quickSignup(payload: {
  name: string;
  email: string;
}): Promise<ApiResponse<AuthResponse>> {
  return request<AuthResponse>("/auth/quick-signup", {
    method: "POST",
    json: payload,
    skipAuth: true,
  });
}

export async function getMe(): Promise<ApiResponse<{ user: User }>> {
  return request<{ user: User }>("/auth/me");
}

export async function logout(): Promise<ApiResponse<{ ok: boolean }>> {
  return request<{ ok: boolean }>("/auth/logout", { method: "POST" });
}

// Solicita o link de redefinição de senha por email. Backend sempre devolve
// sucesso (mesmo se o email não existir) — anti-enumeração.
export async function forgotPassword(
  email: string
): Promise<ApiResponse<{ sent: boolean; message: string }>> {
  return request<{ sent: boolean; message: string }>("/auth/forgot-password", {
    method: "POST",
    json: { email },
    skipAuth: true,
  });
}

// Aplica nova senha usando o token recebido por email. Erros possíveis:
// INVALID_TOKEN, TOKEN_USED, TOKEN_EXPIRED, WEAK_PASSWORD.
export async function resetPassword(payload: {
  token: string;
  password: string;
}): Promise<ApiResponse<{ ok: boolean; user: User }>> {
  return request<{ ok: boolean; user: User }>("/auth/reset-password", {
    method: "POST",
    json: payload,
    skipAuth: true,
  });
}

// ─── Gifts ────────────────────────────────────────────────────────────────────

export async function createGift(
  req: CreateGiftRequest
): Promise<ApiResponse<{ gift: Partial<Gift> }>> {
  return request<{ gift: Partial<Gift> }>("/gifts", { method: "POST", json: req });
}

export async function listGifts(): Promise<ApiResponse<{ gifts: Gift[] }>> {
  return request<{ gifts: Gift[] }>("/gifts");
}

export async function getGift(id: string): Promise<ApiResponse<{ gift: Gift }>> {
  return request<{ gift: Gift }>(`/gifts/${encodeURIComponent(id)}`);
}

// Status mínimo do gift via endpoint público — usado pelo polling do
// PaymentSuccessView. Funciona sem JWT (importante: o user pode estar
// voltando do MP sem sessão ativa).
export interface PublicGiftStatus {
  id: string;
  slug: string;
  status: string;
  publishedAt: string | null;
  passwordEnabled: boolean;
}

export async function getPublicGiftStatus(
  giftId: string
): Promise<ApiResponse<{ gift: PublicGiftStatus }>> {
  return request<{ gift: PublicGiftStatus }>(
    `/public/gifts/by-id/${encodeURIComponent(giftId)}/status`,
    { skipAuth: true }
  );
}

// ─── Influenciadores (afiliados) ─────────────────────────────────────────────

export interface PublicInfluencerExistsResponse {
  exists: boolean;
  active?: boolean;
  name?: string;
}

// Usado pela página /i/<slug> antes de salvar o ref. Não exige JWT.
export async function getInfluencerExists(
  slug: string
): Promise<ApiResponse<PublicInfluencerExistsResponse>> {
  return request<PublicInfluencerExistsResponse>(
    `/public/influencers/${encodeURIComponent(slug)}/exists`,
    { skipAuth: true }
  );
}

export async function updateGift(
  id: string,
  req: UpdateGiftRequest
): Promise<ApiResponse<{ gift: Partial<Gift> }>> {
  return request<{ gift: Partial<Gift> }>(`/gifts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: req,
  });
}

export async function deleteGift(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
  return request<{ deleted: boolean }>(`/gifts/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ─── Public Gift ──────────────────────────────────────────────────────────────

export async function getPublicGift(
  slug: string
): Promise<ApiResponse<{ gift: Partial<Gift> }>> {
  return request<{ gift: Partial<Gift> }>(`/public/gifts/${encodeURIComponent(slug)}`, {
    skipAuth: true,
  });
}

export async function unlockGift(
  slug: string,
  req: UnlockGiftRequest
): Promise<
  ApiResponse<{
    unlocked: boolean;
    accessToken: string;
    expiresAt: string;
    /** Backend devolve o gift COMPLETO (sem senha/userId) no unlock.
     *  Frontend usa pra trocar o gift mascarado pelo conteúdo real. */
    gift: Partial<Gift>;
  }>
> {
  return request<{
    unlocked: boolean;
    accessToken: string;
    expiresAt: string;
    gift: Partial<Gift>;
  }>(`/public/gifts/${encodeURIComponent(slug)}/unlock`, {
    method: "POST",
    json: req,
    skipAuth: true,
  });
}

export async function registerView(
  slug: string
): Promise<ApiResponse<{ viewRegistered: boolean; viewId: string }>> {
  const referrer =
    typeof document !== "undefined" ? document.referrer || undefined : undefined;
  return request<{ viewRegistered: boolean; viewId: string }>(
    `/public/gifts/${encodeURIComponent(slug)}/view`,
    { method: "POST", json: { referrer, source: "share_link" }, skipAuth: true }
  );
}

// ─── Checkout leads (funil) ─────────────────────────────────────────────────

export type CheckoutLeadEvent = "reached" | "identified" | "started_checkout";

export interface CheckoutLeadPayload {
  sessionId: string;
  event: CheckoutLeadEvent;
  email?: string;
  plan?: string;
  giftTitle?: string;
  recipientName?: string;
  partnerName?: string;
  templateSlug?: string;
  selectedExperiences?: string[];
  giftId?: string;
  referrerSlug?: string;
  source?: string;
}

// Registra/atualiza um lead de checkout. É analytics best-effort: nunca
// deve atrapalhar o fluxo de pagamento, então engole qualquer erro e não
// lança. O backend faz upsert pelo sessionId.
export async function trackCheckoutLead(
  payload: CheckoutLeadPayload
): Promise<void> {
  try {
    await request("/public/checkout-leads", {
      method: "POST",
      json: payload,
      skipAuth: true,
      timeoutMs: 8_000,
    });
  } catch {
    // silencioso — funil não pode quebrar o checkout
  }
}

// ─── Templates ────────────────────────────────────────────────────────────────

export async function listTemplates(): Promise<ApiResponse<{ templates: GiftTemplate[] }>> {
  return request<{ templates: GiftTemplate[] }>("/templates");
}

// ─── Music Search ─────────────────────────────────────────────────────────────

export interface MusicSearchResult {
  trackId: number;
  trackName: string;
  artistName: string;
  collectionName: string;
  previewUrl?: string;
  artworkUrl60?: string;
  artworkUrl100: string;
  trackViewUrl: string;
  trackTimeMillis: number;
  releaseDate?: string;
}

export async function searchMusic(
  query: string,
  options?: { country?: string; limit?: number; timeoutMs?: number }
): Promise<ApiResponse<{ query: string; count: number; results: MusicSearchResult[] }>> {
  const params = new URLSearchParams({
    query,
    country: options?.country ?? "BR",
    limit: String(options?.limit ?? 8),
  });
  return request<{ query: string; count: number; results: MusicSearchResult[] }>(
    `/music/search?${params.toString()}`,
    { skipAuth: true, timeoutMs: options?.timeoutMs ?? 10_000 }
  );
}

// ─── Payments ─────────────────────────────────────────────────────────────────

export async function createCheckout(
  req: CheckoutRequest
): Promise<ApiResponse<{ payment: Partial<Payment> }>> {
  // Checkout pode demorar mais (gateway externo) — relaxa o timeout default.
  return request<{ payment: Partial<Payment> }>("/payments/checkout", {
    method: "POST",
    json: req,
    timeoutMs: 45_000,
  });
}

// Checkout Pro Mercado Pago — backend cria a preferência, devolve `checkoutUrl`
// e o frontend redireciona o usuário para o MP escolher Pix/cartão/boleto. O
// preço real é calculado pelo backend a partir do `planId` (frontend nunca é
// fonte de verdade de preço).
//
// `checkoutUrl` é a URL canônica que o frontend deve usar: o backend já
// escolheu entre `init_point` (produção) e `sandbox_init_point` (sandbox)
// conforme `MERCADO_PAGO_MODE` e o fallback `MP_SANDBOX_CHECKOUT_URL`. Os
// campos `initPoint`/`sandboxInitPoint` aparecem na resposta apenas como
// metadados de debug — não use diretamente no fluxo de pagamento.
export interface PlanCheckoutResponse {
  giftId: string;
  orderId?: string | null;
  giftPageId?: string | null;
  planId?: string | null;
  status?: string;
  preferenceId?: string | null;
  checkoutUrl: string;
  initPoint?: string | null;
  sandboxInitPoint?: string | null;
  simulated?: boolean;
}

export async function createPlanCheckout(payload: {
  giftId: string;
  planId: string;
  /** Cupom já validado pelo /coupons/validate. Backend re-valida e
   *  aplica o desconto antes de criar a preferência no MP. */
  couponCode?: string;
  /** Slug do influenciador (afiliado) lido do localStorage/cookie. Backend
   *  resolve em influencerId e grava no Payment. Ignora se inválido/inativo. */
  referrerSlug?: string;
}): Promise<ApiResponse<PlanCheckoutResponse>> {
  return request<PlanCheckoutResponse>("/payments/checkout", {
    method: "POST",
    json: payload,
    timeoutMs: 45_000,
  });
}

export async function getPayment(
  id: string
): Promise<ApiResponse<{ payment: Partial<Payment> }>> {
  return request<{ payment: Partial<Payment> }>(`/payments/${encodeURIComponent(id)}`);
}

// Força sync do pagamento com o Mercado Pago no retorno do checkout. Atalho
// pra que o gift vire PUBLISHED na hora, sem esperar o webhook chegar. O
// backend reusa o mesmo pipeline do webhook (consulta MP por SDK, aplica
// status local, dispara publicação). Idempotente.
export async function syncPayment(
  paymentId: string
): Promise<ApiResponse<{ processed?: boolean; reason?: string }>> {
  return request<{ processed?: boolean; reason?: string }>(
    `/payments/${encodeURIComponent(paymentId)}/sync`,
    { method: "POST", json: {}, timeoutMs: 20_000 }
  );
}

// ─── Coupons ──────────────────────────────────────────────────────────────────

export async function validateCoupon(
  req: ValidateCouponRequest
): Promise<
  ApiResponse<{
    valid: boolean;
    coupon?: { code: string; discountType: string; discountValue: number };
  }>
> {
  // Rota pública — user pode estar no checkout antes de logar (quick-signup).
  return request("/coupons/validate", { method: "POST", json: req, skipAuth: true });
}

// ─── QR Code ──────────────────────────────────────────────────────────────────

export async function getQrCode(
  giftId: string
): Promise<ApiResponse<{ giftId: string; shareUrl: string; qrcodePngUrl: string }>> {
  return request(`/gifts/${encodeURIComponent(giftId)}/qrcode`);
}

// ─── AI ───────────────────────────────────────────────────────────────────────

export async function generateLoveLetter(
  req: AiLoveLetterRequest
): Promise<ApiResponse<{ generation: Partial<AiGeneration> }>> {
  // Geração com OpenAI pode ultrapassar 20s — concede mais tempo.
  return request<{ generation: Partial<AiGeneration> }>("/ai/love-letter", {
    method: "POST",
    json: req,
    timeoutMs: 60_000,
  });
}

export async function improveText(
  req: AiImproveTextRequest
): Promise<ApiResponse<{ generation: Partial<AiGeneration> }>> {
  return request<{ generation: Partial<AiGeneration> }>("/ai/improve-text", {
    method: "POST",
    json: req,
    timeoutMs: 60_000,
  });
}

// Gera o conteúdo estruturado de uma experiência (ex: timeline retorna
// { title, subtitle, events: [...] } pronto pra cair em
// experienceData[experienceId]). Sem OPENAI_API_KEY no backend, devolve um
// seed determinístico — o frontend trata os dois casos igual.
export async function generateExperience(
  req: AiGenerateExperienceRequest
): Promise<ApiResponse<{ generation: Partial<AiGeneration> }>> {
  return request<{ generation: Partial<AiGeneration> }>("/ai/experience", {
    method: "POST",
    json: req,
    timeoutMs: 60_000,
  });
}

// ─── Admin (painel /admin) ────────────────────────────────────────────────────

export interface AdminStats {
  revenue: {
    todayCents: number;
    todayCount: number;
    monthCents: number;
    monthCount: number;
  };
  gifts: {
    total: number;
    published: number;
    conversionRate: number;
  };
  payments: {
    pending: number;
  };
  support: {
    unread: number;
  };
  topPlan: { plan: string; count: number } | null;
}

export interface AdminRecentSupport {
  id: string;
  fromAddress: string;
  fromName?: string | null;
  subject?: string | null;
  receivedAt: string;
  readAt?: string | null;
  repliedAt?: string | null;
}

export interface AdminRecentGift {
  id: string;
  title: string;
  slug: string;
  status: string;
  plan: string;
  createdAt: string;
  publishedAt?: string | null;
}

export async function getAdminStats(): Promise<ApiResponse<AdminStats>> {
  return request<AdminStats>("/admin/stats");
}

export async function getAdminRecent(): Promise<
  ApiResponse<{ support: AdminRecentSupport[]; gifts: AdminRecentGift[] }>
> {
  return request<{
    support: AdminRecentSupport[];
    gifts: AdminRecentGift[];
  }>("/admin/recent");
}

// ─── Suporte (admin) ──────────────────────────────────────────────────────────

export interface SupportLinkedUser {
  id: string;
  email: string;
  name: string;
  _count: { gifts: number; payments: number };
}

export interface SupportMessage {
  id: string;
  fromAddress: string;
  fromName?: string | null;
  subject?: string | null;
  text?: string | null;
  html?: string | null;
  receivedAt: string;
  forwardedAt?: string | null;
  readAt?: string | null;
  repliedAt?: string | null;
  /** Cliente vinculado quando o email bate com algum user.email. */
  linkedUser?: SupportLinkedUser | null;
  /** Chave de thread (fromAddress + subject normalizado). */
  threadKey?: string;
}

export async function listSupportMessages(
  params: { take?: number; skip?: number } = {}
): Promise<ApiResponse<{ messages: SupportMessage[]; total: number }>> {
  const qs = new URLSearchParams();
  if (params.take) qs.set("take", String(params.take));
  if (params.skip) qs.set("skip", String(params.skip));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<{ messages: SupportMessage[]; total: number }>(
    `/support/messages${suffix}`
  );
}

export async function markSupportMessageRead(
  id: string
): Promise<ApiResponse<{ message: SupportMessage }>> {
  return request<{ message: SupportMessage }>(
    `/support/messages/${encodeURIComponent(id)}/read`,
    { method: "PATCH", json: {} }
  );
}

export async function markSupportMessageReplied(
  id: string
): Promise<ApiResponse<{ message: SupportMessage }>> {
  return request<{ message: SupportMessage }>(
    `/support/messages/${encodeURIComponent(id)}/replied`,
    { method: "PATCH", json: {} }
  );
}

export async function archiveSupportMessage(
  id: string
): Promise<ApiResponse<{ message: SupportMessage }>> {
  return request<{ message: SupportMessage }>(
    `/support/messages/${encodeURIComponent(id)}/archive`,
    { method: "PATCH", json: {} }
  );
}

// ─── Admin: séries pra gráficos ───────────────────────────────────────────────

export interface AdminSalesPoint {
  date: string;
  cents: number;
  count: number;
}

export interface AdminGiftsPoint {
  date: string;
  created: number;
  published: number;
}

export async function getAdminSalesSeries(
  days = 30
): Promise<ApiResponse<{ days: number; series: AdminSalesPoint[] }>> {
  return request(`/admin/series/sales?days=${days}`);
}

export async function getAdminGiftsSeries(
  days = 30
): Promise<ApiResponse<{ days: number; series: AdminGiftsPoint[] }>> {
  return request(`/admin/series/gifts?days=${days}`);
}

// ─── Admin: usuários ──────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string | null;
  blockedAt: string | null;
  blockedReason: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  _count: { gifts: number; payments: number };
}

export async function listAdminUsers(params: {
  search?: string;
  take?: number;
  skip?: number;
} = {}): Promise<ApiResponse<{ users: AdminUser[]; total: number }>> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.take) qs.set("take", String(params.take));
  if (params.skip) qs.set("skip", String(params.skip));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request(`/admin/users${suffix}`);
}

export interface AdminUserGiftRef {
  id: string;
  title: string;
  slug: string;
  status: string;
  plan: string;
  createdAt: string;
  publishedAt: string | null;
}

export interface AdminUserPaymentRef {
  id: string;
  status: string;
  method: string;
  finalAmountCents: number;
  createdAt: string;
  paidAt: string | null;
}

export interface AdminUserDetail extends AdminUser {
  gifts: AdminUserGiftRef[];
  payments: AdminUserPaymentRef[];
}

export async function getAdminUser(
  id: string
): Promise<ApiResponse<{ user: AdminUserDetail }>> {
  return request(`/admin/users/${encodeURIComponent(id)}`);
}

export async function setUserBlocked(
  id: string,
  blocked: boolean,
  reason?: string
): Promise<ApiResponse<{ user: { id: string; blockedAt: string | null } }>> {
  return request(`/admin/users/${encodeURIComponent(id)}/blocked`, {
    method: "PATCH",
    json: { blocked, reason },
  });
}

export async function triggerUserPasswordReset(
  id: string
): Promise<ApiResponse<{ reset: { sent?: boolean; reason?: string } }>> {
  return request(`/admin/users/${encodeURIComponent(id)}/reset-link`, {
    method: "POST",
    json: {},
  });
}

export async function impersonateUserApi(
  id: string
): Promise<
  ApiResponse<{
    impersonation: { accessToken: string; expiresInSeconds: number };
  }>
> {
  return request(`/admin/users/${encodeURIComponent(id)}/impersonate`, {
    method: "POST",
    json: {},
  });
}

// ─── Admin: envio manual de email (remarketing) ──────────────────────────
//
// Dispara um email avulso pra um cliente/lead. É manual — o admin escolhe
// um template em /admin/usuarios ou /admin/leads, edita o conteúdo no
// SendEmailDialog e clica em enviar. O frontend já manda assunto e corpo
// totalmente substituídos (sem placeholders {{var}}) — o backend só
// precisa transportar via SMTP. `templateId` vai junto pra log/analytics.
export interface SendAdminEmailPayload {
  to: string;
  subject: string;
  body: string;
  templateId?: string;
  recipientName?: string | null;
  // CTA renderizado como botão grande no email (não como link no corpo).
  // Quando ausente ou inválido, o backend manda sem botão.
  ctaLabel?: string;
  ctaUrl?: string;
}

export async function sendAdminMarketingEmail(
  payload: SendAdminEmailPayload
): Promise<ApiResponse<{ sent: boolean; messageId?: string }>> {
  return request<{ sent: boolean; messageId?: string }>("/admin/email/send", {
    method: "POST",
    json: payload,
  });
}

// ─── Admin: gifts (ILovePages) ────────────────────────────────────────────────

export interface AdminGift {
  id: string;
  title: string;
  slug: string;
  status: string;
  plan: string;
  templateSlug: string | null;
  createdAt: string;
  publishedAt: string | null;
  user: { id: string; name: string; email: string };
  payments: { finalAmountCents: number; paidAt: string | null }[];
}

export async function listAdminGifts(params: {
  search?: string;
  status?: string;
  plan?: string;
  take?: number;
  skip?: number;
} = {}): Promise<ApiResponse<{ gifts: AdminGift[]; total: number }>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request(`/admin/gifts${suffix}`);
}

export interface AdminGiftDetail {
  id: string;
  title: string;
  slug: string;
  status: string;
  plan: string;
  templateSlug: string | null;
  privacyMode: string;
  passwordEnabled: boolean;
  occasion: string;
  recipientName: string | null;
  partnerName: string | null;
  loveLetter: string;
  musicUrl: string | null;
  musicName: string | null;
  artistName: string | null;
  artworkUrl: string | null;
  coupleStartDate: string;
  coverPhotoUrl: string | null;
  selectedExperiences: string[];
  experienceData: Record<string, unknown>;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; name: string; email: string; phone: string | null };
  payments: AdminPayment[];
  photos: { id: string; url: string; order: number }[];
  timelineEvents: { id: string; title: string; eventDate: string }[];
  _count: { views: number };
  adminLogs: AdminLog[];
}

export async function getAdminGift(
  id: string
): Promise<ApiResponse<{ gift: AdminGiftDetail }>> {
  return request(`/admin/gifts/${encodeURIComponent(id)}`);
}

export async function forcePublishGift(
  id: string
): Promise<ApiResponse<{ gift: unknown }>> {
  return request(`/admin/gifts/${encodeURIComponent(id)}/force-publish`, {
    method: "POST",
    json: {},
  });
}

export async function unpublishGift(
  id: string
): Promise<ApiResponse<{ gift: unknown }>> {
  return request(`/admin/gifts/${encodeURIComponent(id)}/unpublish`, {
    method: "POST",
    json: {},
  });
}

export async function extendGiftValidity(
  id: string,
  hours: number
): Promise<ApiResponse<{ gift: unknown }>> {
  return request(`/admin/gifts/${encodeURIComponent(id)}/extend`, {
    method: "POST",
    json: { hours },
  });
}

export async function setGiftPlan(
  id: string,
  plan: "BASIC" | "PREMIUM" | "DELUXE"
): Promise<ApiResponse<{ gift: { id: string; plan: string; status: string } }>> {
  return request(`/admin/gifts/${encodeURIComponent(id)}/plan`, {
    method: "PATCH",
    json: { plan },
  });
}

export async function deleteAdminGift(
  id: string
): Promise<ApiResponse<{ deleted: boolean }>> {
  return request(`/admin/gifts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ─── Admin: pagamentos ────────────────────────────────────────────────────────

export interface AdminPayment {
  id: string;
  status: string;
  method: string;
  provider: string;
  finalAmountCents: number;
  discountCents: number;
  couponCode: string | null;
  providerReference: string | null;
  mercadoPagoPreferenceId: string | null;
  createdAt: string;
  paidAt: string | null;
  gift: { id: string; title: string; slug: string; plan: string };
  user: { id: string; name: string; email: string };
}

export async function listAdminPayments(params: {
  search?: string;
  status?: string;
  take?: number;
  skip?: number;
} = {}): Promise<ApiResponse<{ payments: AdminPayment[]; total: number }>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request(`/admin/payments${suffix}`);
}

export interface AdminPaymentDetail extends AdminPayment {
  amountCents: number;
  currency: string;
  expiresAt: string | null;
  pixQrCodeBase64: string | null;
  pixCopyPaste: string | null;
  checkoutUrl: string | null;
  user: { id: string; name: string; email: string; phone: string | null };
  gift: {
    id: string;
    title: string;
    slug: string;
    status: string;
    plan: string;
    publishedAt: string | null;
  };
  adminLogs: AdminLog[];
}

export async function getAdminPayment(
  id: string
): Promise<ApiResponse<{ payment: AdminPaymentDetail }>> {
  return request(`/admin/payments/${encodeURIComponent(id)}`);
}

export async function manuallyApprovePayment(
  id: string
): Promise<ApiResponse<{ payment: unknown }>> {
  return request(`/admin/payments/${encodeURIComponent(id)}/manual-approve`, {
    method: "POST",
    json: {},
  });
}

// ─── Admin: planos ────────────────────────────────────────────────────────────

export interface AdminPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  priceCents: number;
  durationHours: number | null;
  features: string[];
  badge: string | null;
  highlight: boolean;
  active: boolean;
  displayOrder: number;
}

export async function listAdminPlans(): Promise<
  ApiResponse<{ plans: AdminPlan[] }>
> {
  return request("/admin/plans");
}

export async function updateAdminPlan(
  id: string,
  patch: Partial<AdminPlan>
): Promise<ApiResponse<{ plan: AdminPlan }>> {
  return request(`/admin/plans/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: patch,
  });
}

// ─── Admin: templates ─────────────────────────────────────────────────────────

export interface AdminTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  isPremium: boolean;
  planRequired: string;
  isActive: boolean;
  featured: boolean;
  displayOrder: number;
  usageCount: number;
  createdCount?: number;
  conversionRate?: number;
  config?: Record<string, unknown>;
}

export async function listAdminTemplates(): Promise<
  ApiResponse<{ templates: AdminTemplate[] }>
> {
  return request("/admin/templates");
}

export async function updateAdminTemplate(
  id: string,
  patch: Partial<AdminTemplate>
): Promise<ApiResponse<{ template: AdminTemplate }>> {
  return request(`/admin/templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: patch,
  });
}

// ─── Admin: experiências ──────────────────────────────────────────────────────

export interface AdminExperience {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  iconKey: string | null;
  minimumPlan: string;
  active: boolean;
  displayOrder: number;
}

export async function listAdminExperiences(): Promise<
  ApiResponse<{ experiences: AdminExperience[] }>
> {
  return request("/admin/experiences");
}

export async function updateAdminExperience(
  id: string,
  patch: Partial<AdminExperience>
): Promise<ApiResponse<{ experience: AdminExperience }>> {
  return request(`/admin/experiences/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: patch,
  });
}

// ─── Admin: cupons ────────────────────────────────────────────────────────────

export interface AdminCoupon {
  id: string;
  code: string;
  description: string | null;
  discountType: "PERCENT" | "FIXED";
  discountValue: number;
  maxRedemptions: number | null;
  redemptions: number;
  isActive: boolean;
  validFrom: string;
  validUntil: string | null;
  applicablePlans: string[];
  createdAt: string;
}

export async function listAdminCoupons(): Promise<
  ApiResponse<{ coupons: AdminCoupon[] }>
> {
  return request("/admin/coupons");
}

export async function createAdminCoupon(
  payload: Partial<AdminCoupon>
): Promise<ApiResponse<{ coupon: AdminCoupon }>> {
  return request("/admin/coupons", { method: "POST", json: payload });
}

export async function updateAdminCoupon(
  id: string,
  patch: Partial<AdminCoupon>
): Promise<ApiResponse<{ coupon: AdminCoupon }>> {
  return request(`/admin/coupons/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function deleteAdminCoupon(
  id: string
): Promise<ApiResponse<{ deleted: boolean }>> {
  return request(`/admin/coupons/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ─── Admin: influenciadores (afiliados) ──────────────────────────────────────

export interface AdminInfluencerStats {
  salesCount: number;
  grossRevenueCents: number;
  commissionCents: number;
}

export interface AdminInfluencer {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  commissionPct: number;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  stats: AdminInfluencerStats;
}

export interface AdminInfluencerPayment {
  id: string;
  status: string;
  method: string;
  finalAmountCents: number;
  discountCents: number;
  couponCode: string | null;
  paidAt: string | null;
  createdAt: string;
  gift: {
    id: string;
    slug: string;
    title: string;
    plan: string;
    status: string;
  } | null;
}

export interface AdminInfluencerDetail extends AdminInfluencer {
  payments: AdminInfluencerPayment[];
}

export async function listAdminInfluencers(): Promise<
  ApiResponse<{ influencers: AdminInfluencer[] }>
> {
  return request("/admin/influencers");
}

export async function getAdminInfluencer(
  id: string
): Promise<ApiResponse<{ influencer: AdminInfluencerDetail }>> {
  return request(`/admin/influencers/${encodeURIComponent(id)}`);
}

export async function createAdminInfluencer(payload: {
  name: string;
  slug?: string;
  email?: string | null;
  commissionPct?: number;
  active?: boolean;
  notes?: string | null;
}): Promise<ApiResponse<{ influencer: AdminInfluencer }>> {
  return request("/admin/influencers", { method: "POST", json: payload });
}

export async function updateAdminInfluencer(
  id: string,
  patch: Partial<Pick<AdminInfluencer, "name" | "slug" | "email" | "commissionPct" | "active" | "notes">>
): Promise<ApiResponse<{ influencer: AdminInfluencer }>> {
  return request(`/admin/influencers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function deleteAdminInfluencer(
  id: string
): Promise<ApiResponse<{ deactivated: boolean }>> {
  return request(`/admin/influencers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ─── Admin: configurações + logs ──────────────────────────────────────────────

export interface AdminEditableSettings {
  brand_name: string;
  support_whatsapp: string;
  support_email: string;
  support_hours: string;
}

export interface AdminSettings {
  mercadoPago: {
    mode: string;
    hasToken: boolean;
    lastWebhookSuccess: string | null;
    lastWebhookError: string | null;
  };
  resend: {
    hasApiKey: boolean;
    lastEmailSentAt: string | null;
    lastInboundAt: string | null;
  };
  openai: {
    hasApiKey: boolean;
    model: string;
  };
  gemini?: {
    hasApiKey: boolean;
    model: string;
  };
  editable: AdminEditableSettings;
}

export async function getAdminSettings(): Promise<ApiResponse<AdminSettings>> {
  return request("/admin/settings");
}

export async function updateAdminSetting(
  key: keyof AdminEditableSettings,
  value: string
): Promise<ApiResponse<{ setting: { key: string; value: string } }>> {
  return request(`/admin/settings/${encodeURIComponent(key)}`, {
    method: "PATCH",
    json: { value },
  });
}

export interface AdminLog {
  id: string;
  adminId: string;
  action: string;
  level: "info" | "warn" | "error";
  giftId: string | null;
  paymentId: string | null;
  targetUserId: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export async function listAdminLogs(params: {
  action?: string;
  level?: string;
  take?: number;
  skip?: number;
} = {}): Promise<ApiResponse<{ logs: AdminLog[]; total: number }>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request(`/admin/logs${suffix}`);
}

// ─── Admin: leads de checkout ─────────────────────────────────────────────────

export interface AdminCheckoutLead {
  id: string;
  status: "REACHED" | "IDENTIFIED" | "STARTED_CHECKOUT";
  email: string | null;
  plan: string | null;
  giftTitle: string | null;
  recipientName: string | null;
  partnerName: string | null;
  templateSlug: string | null;
  giftId: string | null;
  referrerSlug: string | null;
  source: string | null;
  identifiedAt: string | null;
  startedCheckoutAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Marca se o gift associado já teve pagamento aprovado.
  paid: boolean;
}

export interface AdminLeadCounts {
  REACHED: number;
  IDENTIFIED: number;
  STARTED_CHECKOUT: number;
}

export async function listAdminCheckoutLeads(params: {
  search?: string;
  status?: string;
  take?: number;
  skip?: number;
} = {}): Promise<
  ApiResponse<{
    leads: AdminCheckoutLead[];
    total: number;
    counts: AdminLeadCounts;
  }>
> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request(`/admin/leads${suffix}`);
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export async function getDashboardSummary(): Promise<
  ApiResponse<{ totalGifts: number; publishedGifts: number; totalViews: number; revenue: number }>
> {
  return request("/dashboard/summary");
}

// ─── Photos (upload multipart) ────────────────────────────────────────────────

export async function uploadPhoto(
  giftId: string,
  file: File,
  order: number
): Promise<ApiResponse<{ photo: { id: string; url: string; order: number } }>> {
  const form = new FormData();
  form.append("file", file);
  form.append("order", String(order));
  return request<{ photo: { id: string; url: string; order: number } }>(
    `/gifts/${encodeURIComponent(giftId)}/photos`,
    { method: "POST", formData: form, timeoutMs: 60_000 }
  );
}

// Upload público de imagem (pré-pagamento) — usado por experiências/galeria
// pra subir fotos antes do gift existir no banco. Devolve URL curta que
// substitui o data URL base64 gigante no experienceData — evita JSON
// estourar o limit do Express (1 MB) e o nginx (12 MB).
export interface UploadImageResponse {
  url: string;
  filename: string;
  key?: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
  mimeType?: string;
}

export async function uploadImage(
  file: File
): Promise<ApiResponse<UploadImageResponse>> {
  const form = new FormData();
  form.append("image", file);
  return request<UploadImageResponse>("/uploads/images", {
    method: "POST",
    formData: form,
    skipAuth: true,
    timeoutMs: 60_000,
  });
}
